// User Info
import React, {useState} from 'react';
import {useSelector} from 'react-redux';

// Icons & Framework
import * as Icon from 'ubs-icons';
import {COLOR} from "ubs-framework/v2/common/constant";
import {copyLink, isInApp, getThumbor} from "ubs-framework/v2/common/function";
import UCustomScrollBar from 'ubs-framework/v2/UCustomScrollBar';
import UAvatar from "ubs-framework/v2/UAvatar";
import UFilePreview from "ubs-framework/v2/UFileView/file-preview";
import { useFieldLabel } from 'ubs-framework/v2/hooks/useCustomFieldConfig';
import { PROFILE_SECTION } from 'ubs-framework/v2/common/constant';

// Components
import UPopup from "ubs-framework/v2/UPopup";
import Skeleton from "ubs-framework/v2/skeleton";
import {onLogout, sendData} from "../../../../store/actions";
import {isSupportPortalEligible} from 'ubs-framework/v2/common/function';
import USupportButton from 'ubs-framework/v2/USupportButton';
import useSocket from "../../hooks/useSocket";

const UserInfo = () => {
    const getLabel = useFieldLabel();

    const [showPreview, setShowPreview] = useState(false);

    const _U_EMAIL = useSelector(e => e.common.email);
    const _UI = useSelector(e => e.common?.userInfo);
    const _U_ID = useSelector(e => e.common.user_id);
    const _WS = useSelector(e => e.common.workSpaces);
    const _JT = useSelector(e => e.common.jobTitle);
    const _ORG_ID = useSelector(e => e.common.orgId);
    // Support portal: company flag + computed eligibility (owner / System Admin / all-employees Manager).
    const _SUPPORT_PORTAL = useSelector(e => e.common.supportModuleEnabled);
    const _IS_ADMIN = useSelector(e => e.common.isAdmin);
    const _UAC = useSelector(e => e.common.uac);
    const _SUPPORT_ELIGIBLE = isSupportPortalEligible({ isAdmin: _IS_ADMIN, userType: _UAC?.userType, employeeVisibility: _UAC?.employeeVisibility });
    const _PLAN = useSelector(e => e?.common?.planAccess);
    const _S3_BASE_URL = useSelector(e => e.common.S3_BASE_URL);

    const switchOrganisation = orgId => {
        if (_ORG_ID !== orgId) {
            window.open(`${window.location.origin}/${orgId}`, '_blank');
        }
    };

    const gotoChat = () => {
        if (_PLAN?.chat?.default?.access) window.open(`${process.env.REACT_APP_CHAT_MODULE_URL}/${_ORG_ID}/chat#pm/${_U_ID}`, '_blank');
    };

    const copyMessage = () => {
        copyLink(`${process.env.REACT_APP_CHAT_MODULE_URL}/${_ORG_ID}/chat#pm/${_U_ID}`, 'Chat')
    };

    return (
        <React.Fragment>
            <UCustomScrollBar>
                <div className="u_user-info-header u_user-info-sidebar">
                    <div className="u_profile-info">
                        <div className="u_profile-info-user">

                            <div className="u_profile-info-user__avatar u_mb-10">
                                {/* Profile Image */}
                                {_UI?.name ?
                                    <React.Fragment>
                                        <div className="u_userimage u_cp" onClick={() => setShowPreview(true)}>
                                            <UAvatar
                                                name={_UI?.name}
                                                image={_UI?.profileImage}
                                                width={80}
                                                s3Url={_S3_BASE_URL}
                                                thumbor={getThumbor('96x96')}
                                            />
                                            <span className="u_eye-icon u_color-white">
                                                <Icon.Eye fill={COLOR.u_white} size={18}/>
                                            </span>
                                        </div>
                                        {showPreview && <UFilePreview
                                            file={{
                                                url: _UI?.profileImage,
                                                fileName: _UI?.name
                                            }}
                                            onClose={() => setShowPreview(false)}
                                            hideDownload
                                        />}
                                    </React.Fragment>
                                    :
                                    <Skeleton width='80px' height="80px" rounded />
                                }
                            </div>

                            {/* Name */}
                            <div className="u_profile-info-user__name u_text-center">
                                <h5 className="u_one-line u_table-two-line u_m-auto">
                                    {_UI?.name ? _UI?.name : <Skeleton width='170px' height="22px" className="u_mx-auto"/>}
                                </h5>
                                {_JT?.name && <p className="u_designation-title">{_JT?.name}</p>}
                            </div>

                        </div>

                    </div>

                    {_PLAN?.chat?.default?.access && <div className="u_user-info-options">
                        <ul>
                            {!isInApp() && <li className="u_icon-round">
                                <UPopup
                                    params={{className: 'u_tooltip'}}
                                    desc={<span>Direct Message</span>}
                                >
                                    <span onClick={gotoChat}>
                                        <Icon.Comment size={18} fill={COLOR.u_icon_bg}/>
                                    </span>
                                </UPopup>
                            </li>}
                            <li className="u_icon-round">
                                <UPopup
                                    params={{className: 'u_tooltip'}}
                                    desc={<span>Copy Link</span>}
                                >
                                    <span onClick={copyMessage}>
                                        <Icon.Link size={16} fill={COLOR.u_icon_bg}/>
                                    </span>
                                </UPopup>
                            </li>
                        </ul>
                    </div>}

                </div>

                <div className="u_user-info-details">
                    <ul>
                        {_U_EMAIL && <li>
                            <Icon.Mail size={16} fill={COLOR.u_icon_bg}/>
                            <p>{_U_EMAIL}</p>

                            <UPopup
                                params={{className: 'u_tooltip'}}
                                desc={<span>Copy {getLabel(PROFILE_SECTION.emergencyContact, 'email', 'Email')} Address</span>}
                            >
                                <span className="u_user-info-details-copy u_cp u_lh-0"
                                      onClick={() => copyLink(_U_EMAIL, 'Email')}>
                                    <Icon.Copy size={14} fill={COLOR.u_blue}/>
                                </span>
                            </UPopup>
                        </li>}
                    </ul>
                </div>

                <div className="u_user-info-org">
                    <div className="u_content-subheader u_flex u_align-center u_justify-between u_px-15">
                        <h5>Organizations</h5>
                    </div>


                    <ul className="u_user-info-org-list u_user-info-details">
                        {
                            _WS.map((data, i) => {
                                return <li key={data.orgId} onClick={() => switchOrganisation(data.orgId)}
                                           className={`u_cp ${_ORG_ID === data.orgId ? 'u_active' : ''}`}>
                                    {_ORG_ID === data.orgId && <Icon.CheckMark fill={COLOR.u_green} size={16}/>}
                                    <p>
                                        <span>{data.company}</span>
                                    </p>
                                </li>
                            })
                        }
                    </ul>
                </div>

            </UCustomScrollBar>

            <div className="u_user-info-footer">
                <USupportButton enabled={_SUPPORT_PORTAL} eligible={_SUPPORT_ELIGIBLE} sendData={sendData} useSocket={useSocket}/>
                <p className="u_logout-text" onClick={() => onLogout()}>
                    <Icon.PowerButton fill="#FE6F5E" size={18} className="u_mr-10"/>Logout</p>
            </div>
        </React.Fragment>
    )
}

export default UserInfo;
